import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

export interface DuplicateCandidate {
  comparisonFields?: Array<{
    key: string;
    label: string;
    entityAValue: string | null;
    entityBValue: string | null;
    recommended: "entityA" | "entityB";
  }>;
  recommendedWinner?: "entityA" | "entityB";
  id: string;
  entityAId: string;
  entityBId: string;
  entityAName: string;
  entityAEmail: string;
  entityAPhone?: string;
  entityBName: string;
  entityBEmail: string;
  entityBPhone?: string;
  confidence: number;
  matchReasons: string[];
  status: "pending" | "merged" | "dismissed";
  createdAt: string;
}

export interface DuplicateScanResult {
  matches: Array<{
    entityAId: string;
    entityBId: string;
    confidence: number;
    matchReasons: string[];
  }>;
  autoMerged: Array<{
    candidateId: string;
    winnerId: string;
    loserId: string;
  }>;
}

export function useCrmDuplicates() {
  const [data, setData] = useState<DuplicateCandidate[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiFetch<{ data: DuplicateCandidate[] }>("/crm/intelligence/duplicates")
      .then((res) => setData(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}

export async function mergeCrmDuplicate(
  candidateId: string,
  input: {
    entityAId: string;
    entityBId: string;
    winnerSource: "entityA" | "entityB";
    fieldSelections?: Record<string, "entityA" | "entityB">;
  },
) {
  const result = await apiFetch<{ data: { merged: boolean; mergeHistoryId: string } }>(
    `/crm/intelligence/duplicates/${candidateId}/merge`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  return result.data;
}

export async function dismissCrmDuplicate(candidateId: string) {
  const result = await apiFetch<{ data: { dismissed: boolean } }>(
    `/crm/intelligence/duplicates/${candidateId}/dismiss`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
  return result.data;
}

export async function undoCrmMerge(mergeHistoryId: string) {
  const result = await apiFetch<{ data: { undone: boolean; restoredDealCount: number } }>(
    `/crm/intelligence/merges/${mergeHistoryId}/undo`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
  return result.data;
}

export async function scanCrmContactForDuplicates(contactId: string) {
  const result = await apiFetch<{ data: DuplicateScanResult }>(
    `/crm/intelligence/contacts/${contactId}/dedup-scan`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
  return result.data;
}
